import { useEffect, useState } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, Plus, Play, Trash2, KeyRound, X, CheckCircle2, Download, Upload, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  WORKING: "bg-[#30D158]/15 text-[#30D158]",
  UNCONFIGURED: "bg-white/10 text-[#A1A1AA]",
  AUTHENTICATION_FAILED: "bg-[#FF453A]/15 text-[#FF8A80]",
  RATE_LIMITED: "bg-[#FF9F0A]/15 text-[#FF9F0A]",
  PROVIDER_UNAVAILABLE: "bg-[#FF9F0A]/15 text-[#FF9F0A]",
  MODEL_INVALID: "bg-[#FF453A]/15 text-[#FF8A80]",
  GENERATION_FAILED: "bg-[#FF453A]/15 text-[#FF8A80]",
};

const EMPTY = {
  name: "", category: "Image Generation", pricing_type: "FREE", auth_type: "none",
  execution_mode: "sync", priority: 20, enabled: true, base_url: "",
  description: "", website_url: "", documentation_url: "",
  auth_config: { token_secret: "", header_name: "", query_param: "" },
  request_config: { method: "POST", endpoint: "", body: "", query: "" },
  response_config: { output_type: "url", output_path: "", job_id_path: "", error_path: "" },
  polling_config: { status_endpoint: "", status_path: "", success_values: "", failure_values: "", interval_s: 1, max_attempts: 25, result_path: "" },
  secrets: "",
  models: "",
};

function parseMaybeJSON(s, fallback) {
  if (!s || !s.trim()) return fallback;
  try { return JSON.parse(s); } catch { return fallback; }
}

export default function CustomProviders() {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ categories: [], pricing_types: [], auth_types: [], execution_modes: [] });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testRes, setTestRes] = useState({});
  const [secretDraft, setSecretDraft] = useState({});
  const [cfDiag, setCfDiag] = useState(null);
  const [busy, setBusy] = useState("");

  const exportCfg = async () => {
    try {
      const d = await api.customExport();
      const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = "caption-motion-providers.json"; a.click();
      toast.success("Exported (secrets redacted as {{SECRET:...}})");
    } catch { toast.error("Export failed"); }
  };
  const importCfg = async (file) => {
    if (!file) return;
    setBusy("import");
    try {
      const txt = await file.text();
      const r = await api.customImport(JSON.parse(txt));
      toast.success(`Imported ${r.imported.length} provider(s)`);
      if (r.missing_secrets?.length) toast.warning(`Configure secrets: ${r.missing_secrets.join(", ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed (invalid JSON?)"); }
    setBusy("");
  };
  const diagnoseCF = async () => {
    setBusy("cf"); setCfDiag(null);
    try { const r = await api.cloudflareDiagnose(); setCfDiag(r); r.ok ? toast.success("Cloudflare OK") : toast.error(r.verdict); }
    catch (e) { toast.error(e?.response?.data?.detail || "Diagnose failed"); }
    setBusy("");
  };

  const load = async () => {
    try { const r = await api.customProviders(); setData(r.providers); setMeta(r); }
    catch (e) { toast.error("Failed to load custom providers"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (grp, k, v) => setForm((f) => ({ ...f, [grp]: { ...f[grp], [k]: v } }));

  const create = async () => {
    if (!form.name.trim() || !form.base_url.trim()) { toast.error("Name and Base URL are required"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name, category: form.category, pricing_type: form.pricing_type,
        auth_type: form.auth_type, execution_mode: form.execution_mode,
        priority: Number(form.priority) || 20, enabled: !!form.enabled, base_url: form.base_url,
        description: form.description, website_url: form.website_url, documentation_url: form.documentation_url,
        auth_config: form.auth_config,
        request_config: {
          method: form.request_config.method,
          endpoint: form.request_config.endpoint,
          body: form.request_config.body || undefined,
          query: parseMaybeJSON(form.request_config.query, undefined),
        },
        response_config: form.response_config,
        polling_config: form.execution_mode === "async" ? {
          ...form.polling_config,
          success_values: (form.polling_config.success_values || "").split(",").map((s) => s.trim()).filter(Boolean),
          failure_values: (form.polling_config.failure_values || "").split(",").map((s) => s.trim()).filter(Boolean),
        } : {},
        secrets: parseMaybeJSON(form.secrets, []),
        models: parseMaybeJSON(form.models, []),
      };
      await api.customProviderCreate(body);
      toast.success("Provider created");
      setShowAdd(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    setSaving(false);
  };

  const runTest = async (pid) => {
    setTesting(pid); setTestRes((t) => ({ ...t, [pid]: null }));
    try {
      const r = await api.customProviderTest(pid);
      setTestRes((t) => ({ ...t, [pid]: r }));
      r.ok ? toast.success(`${r.name}: WORKING (${r.latencyMs}ms)`) : toast.error(`${r.status}: ${r.safeErrorMessage || "failed"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    setTesting(null);
  };

  const toggle = async (p) => {
    try { await api.customProviderUpdate(p.id, { enabled: !p.enabled }); load(); }
    catch (e) { toast.error("Update failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete provider "${p.name}"?`)) return;
    try { await api.customProviderDelete(p.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const saveSecrets = async (p) => {
    const values = {};
    (p.secrets || []).forEach((s) => { const v = secretDraft[`${p.id}.${s.env}`]; if (v) values[s.env] = v; });
    if (!Object.keys(values).length) { toast.error("Enter at least one secret value"); return; }
    try { await api.customProviderSecrets(p.id, values); toast.success("Secrets saved (encrypted)"); setSecretDraft({}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;

  return (
    <div className="mt-8" data-testid="custom-providers">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-black text-white">Custom Providers <span className="text-xs font-semibold text-[#A1A1AA]">— add any AI API from here, no code</span></h3>
          <p className="text-xs text-[#A1A1AA]">Configure a new provider's endpoint, auth, request &amp; response mapping. It runs through the generic engine and joins LIVE generation by priority.</p>
        </div>
        <button data-testid="cp-add-btn" onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-3 py-2 text-xs font-bold text-black hover:bg-[#00F0FF]/85">
          <Plus size={14} /> Add Provider
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button data-testid="cp-export-btn" onClick={exportCfg} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#00F0FF]/60"><Download size={13} /> Export</button>
        <label data-testid="cp-import-btn" className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#00F0FF]/60">
          {busy === "import" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Import
          <input type="file" accept="application/json" className="hidden" onChange={(e) => importCfg(e.target.files?.[0])} />
        </label>
        <button data-testid="cp-cfdiag-btn" onClick={diagnoseCF} disabled={busy === "cf"} className="flex items-center gap-1.5 rounded-lg border border-[#FF9F0A]/40 px-3 py-1.5 text-xs font-semibold text-[#FF9F0A] hover:bg-white/5 disabled:opacity-50">
          {busy === "cf" ? <Loader2 size={13} className="animate-spin" /> : <Stethoscope size={13} />} Diagnose Cloudflare
        </button>
      </div>

      {cfDiag && (
        <div className="mb-4 rounded-xl border border-white/10 bg-black/30 p-4" data-testid="cp-cfdiag-result">
          <div className="mb-2 text-xs font-bold text-white">Cloudflare Workers AI — staged diagnosis: <span className={cfDiag.ok ? "text-[#30D158]" : "text-[#FF8A80]"}>{cfDiag.verdict}</span></div>
          <div className="space-y-1">
            {(cfDiag.stages || []).map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-[11px]">
                <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${s.ok ? "bg-[#30D158]" : "bg-[#FF453A]"}`} />
                <span className="w-24 shrink-0 font-mono font-bold text-[#A1A1AA]">{s.stage}</span>
                <span className="text-[#D4D4D8]">{s.message}{s.http ? ` (HTTP ${s.http})` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(data || []).length === 0 && <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-[#A1A1AA]">No custom providers yet. Click <b className="text-white">Add Provider</b> to configure one.</div>}
        {(data || []).map((p) => {
          const tr = testRes[p.id];
          const st = p.health_status || "UNCONFIGURED";
          return (
            <div key={p.id} data-testid={`cp-card-${p.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-display text-base font-black text-white">{p.name}</span>
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold text-[#A1A1AA]">{p.category}</span>
                  <span className="rounded-full bg-[#8B5CF6]/15 px-2 py-0.5 text-[10px] font-bold text-[#B794F6]">{p.pricing_type}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLE[st] || "bg-white/10 text-[#A1A1AA]"}`} data-testid={`cp-status-${p.id}`}>{st}</span>
                  {p.enabled ? <span className="text-[10px] font-bold text-[#30D158]">ENABLED</span> : <span className="text-[10px] font-bold text-[#A1A1AA]">DISABLED</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button data-testid={`cp-test-${p.id}`} onClick={() => runTest(p.id)} disabled={testing === p.id}
                    className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white hover:border-[#00F0FF]/60 disabled:opacity-40">
                    {testing === p.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Test
                  </button>
                  <button data-testid={`cp-toggle-${p.id}`} onClick={() => toggle(p)} className="rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white">{p.enabled ? "Disable" : "Enable"}</button>
                  <button data-testid={`cp-delete-${p.id}`} onClick={() => del(p)} className="rounded-lg border border-[#FF453A]/40 p-1.5 text-[#FF453A] hover:bg-white/5"><Trash2 size={13} /></button>
                </div>
              </div>
              <div className="mt-1 font-mono text-[11px] text-[#A1A1AA]">{p.request_config?.method || "POST"} {p.base_url}{p.request_config?.endpoint}</div>
              {(p.secrets || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3">
                  <div className="mb-1 flex items-center gap-1 text-[11px] font-bold text-[#A1A1AA]"><KeyRound size={11} /> SECRETS (encrypted)</div>
                  {(p.secrets || []).map((s) => (
                    <div key={s.env} className="mb-1.5 flex items-center gap-2">
                      <span className="w-40 shrink-0 font-mono text-[11px] text-[#A1A1AA]">{s.env}</span>
                      <input type="password" data-testid={`cp-secret-${p.id}-${s.env}`} autoComplete="new-password"
                        placeholder={s.configured ? "•••• configured" : "Enter value"}
                        value={secretDraft[`${p.id}.${s.env}`] || ""} onChange={(e) => setSecretDraft((d) => ({ ...d, [`${p.id}.${s.env}`]: e.target.value }))}
                        className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-xs text-white outline-none focus:border-[#00F0FF]/50" />
                      {s.configured && <CheckCircle2 size={13} className="text-[#30D158]" />}
                    </div>
                  ))}
                  <button data-testid={`cp-savesecrets-${p.id}`} onClick={() => saveSecrets(p)} className="mt-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black">Save Secrets</button>
                </div>
              )}
              {tr && (
                <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3 text-[11px]">
                  <div className="flex flex-wrap gap-3 text-[#A1A1AA]">
                    <span>status: <b className={tr.ok ? "text-[#30D158]" : "text-[#FF8A80]"}>{tr.status}</b></span>
                    {tr.httpStatus != null && <span>http: {tr.httpStatus}</span>}
                    {tr.failureStage && <span>stage: {tr.failureStage}</span>}
                    {tr.latencyMs != null && <span>{tr.latencyMs}ms</span>}
                    {tr.model && <span>model: {tr.model}</span>}
                  </div>
                  {tr.safeErrorMessage && <div className="mt-1 text-[#FF8A80]">{tr.safeErrorMessage}</div>}
                  {tr.url && <img src={`${BACKEND}${tr.url}`} alt="test" className="mt-2 h-32 w-32 rounded-lg object-cover" data-testid={`cp-testimg-${p.id}`} />}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-6" data-testid="cp-add-modal">
          <div className="relative my-4 w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0E0E12] p-5">
            <WindowSearch />
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-display text-base font-black text-white">Add Provider</h4>
              <button onClick={() => setShowAdd(false)} className="text-[#A1A1AA] hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <L label="Provider Name"><I testid="cp-f-name" v={form.name} on={(v) => set("name", v)} ph="Free Image API" /></L>
              <L label="Category"><S testid="cp-f-category" v={form.category} on={(v) => set("category", v)} opts={meta.categories} /></L>
              <L label="Pricing Type"><S testid="cp-f-pricing" v={form.pricing_type} on={(v) => set("pricing_type", v)} opts={meta.pricing_types} /></L>
              <L label="Priority"><I testid="cp-f-priority" type="number" v={form.priority} on={(v) => set("priority", v)} /></L>
              <L label="Base API URL" full><I testid="cp-f-baseurl" v={form.base_url} on={(v) => set("base_url", v)} ph="https://api.example.com" /></L>
              <L label="Auth Type"><S testid="cp-f-auth" v={form.auth_type} on={(v) => set("auth_type", v)} opts={meta.auth_types} /></L>
              <L label="Execution Mode"><S testid="cp-f-mode" v={form.execution_mode} on={(v) => set("execution_mode", v)} opts={meta.execution_modes} /></L>
              {form.auth_type !== "none" && form.auth_type !== "basic" && (
                <L label="Auth Secret env (e.g. API_KEY)"><I testid="cp-f-tokensecret" v={form.auth_config.token_secret} on={(v) => setNested("auth_config", "token_secret", v.toUpperCase())} ph="MYPROVIDER_KEY" /></L>
              )}
              {form.auth_type === "api_key_header" && <L label="Header name"><I v={form.auth_config.header_name} on={(v) => setNested("auth_config", "header_name", v)} ph="x-api-key" /></L>}
              {form.auth_type === "api_key_query" && <L label="Query param"><I v={form.auth_config.query_param} on={(v) => setNested("auth_config", "query_param", v)} ph="key" /></L>}
              <L label="HTTP Method"><S testid="cp-f-method" v={form.request_config.method} on={(v) => setNested("request_config", "method", v)} opts={["POST", "GET", "PUT", "PATCH", "DELETE"]} /></L>
              <L label="Endpoint (after base URL)"><I testid="cp-f-endpoint" v={form.request_config.endpoint} on={(v) => setNested("request_config", "endpoint", v)} ph="/v1/images  or  /prompt/{{PROMPT}}" /></L>
              <L label="Request body template (JSON, use {{PROMPT}} {{WIDTH}} {{MODEL}}…)" full>
                <T testid="cp-f-body" v={form.request_config.body} on={(v) => setNested("request_config", "body", v)} ph='{ "prompt": "{{PROMPT}}", "width": {{WIDTH}}, "height": {{HEIGHT}} }' />
              </L>
              <L label="Query params (JSON, optional)" full><T testid="cp-f-query" v={form.request_config.query} on={(v) => setNested("request_config", "query", v)} ph='{ "width": "{{WIDTH}}" }' /></L>
              <L label="Response output type"><S testid="cp-f-outtype" v={form.response_config.output_type} on={(v) => setNested("response_config", "output_type", v)} opts={["url", "base64", "binary", "text"]} /></L>
              <L label="Response output path (JSONPath)"><I testid="cp-f-outpath" v={form.response_config.output_path} on={(v) => setNested("response_config", "output_path", v)} ph="$.data[0].url" /></L>
              {form.execution_mode === "async" && <>
                <L label="Job ID path"><I v={form.response_config.job_id_path} on={(v) => setNested("response_config", "job_id_path", v)} ph="$.id" /></L>
                <L label="Status endpoint (use {{JOB_ID}})"><I v={form.polling_config.status_endpoint} on={(v) => setNested("polling_config", "status_endpoint", v)} ph="/status/{{JOB_ID}}" /></L>
                <L label="Status path"><I v={form.polling_config.status_path} on={(v) => setNested("polling_config", "status_path", v)} ph="$.status" /></L>
                <L label="Result path"><I v={form.polling_config.result_path} on={(v) => setNested("polling_config", "result_path", v)} ph="$.result.url" /></L>
                <L label="Success values (comma)"><I v={form.polling_config.success_values} on={(v) => setNested("polling_config", "success_values", v)} ph="succeeded, completed" /></L>
                <L label="Failure values (comma)"><I v={form.polling_config.failure_values} on={(v) => setNested("polling_config", "failure_values", v)} ph="failed, error" /></L>
              </>}
              <L label='Secrets (JSON array, e.g. [{"env":"API_KEY","label":"Key","required":true}])' full><T testid="cp-f-secrets" v={form.secrets} on={(v) => set("secrets", v)} ph='[{"env":"MYPROVIDER_KEY","label":"API Key","required":true}]' /></L>
              <L label='Models (JSON array, optional)' full><T testid="cp-f-models" v={form.models} on={(v) => set("models", v)} ph='[{"name":"v1","model_id":"free-image-v1","enabled":true}]' /></L>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA]">Cancel</button>
              <button data-testid="cp-create-btn" onClick={create} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black disabled:opacity-50">{saving && <Loader2 size={14} className="animate-spin" />} Create Provider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const L = ({ label, full, children }) => (
  <div className={full ? "col-span-2" : ""}>
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA]">{label}</div>
    {children}
  </div>
);
const I = ({ v, on, ph, type = "text", testid }) => (
  <input type={type} data-testid={testid} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={ph}
    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF]/50" />
);
const T = ({ v, on, ph, testid }) => (
  <textarea data-testid={testid} rows={3} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={ph}
    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-xs text-white outline-none focus:border-[#00F0FF]/50" />
);
const S = ({ v, on, opts, testid }) => (
  <select data-testid={testid} value={v} onChange={(e) => on(e.target.value)}
    className="w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white">
    {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);
